import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@foundation/components/ui/button";
import { Badge } from "@foundation/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@foundation/components/ui/card";
import { useUser } from "@foundation/contextapi/UserContext";
import { useToast } from "@foundation/hooks/use-toast";
import { getActiveWorkspaceId } from "../../../header/activeWorkspace";
import {
  getBoostPolicyById,
  updateBoostPolicyStep,
  saveGeneratedContent,
  fetchSavedContent,
  fetchEditorContent,
  loadPolicyTemplateById,
  requestApproval,
  getApprovalState,
} from "../services/boostPolicyService";
import type { PolicyApprovalState } from "../services/boostPolicyService";
import { usePolicyApproval } from "../hooks/usePolicyApproval";
import { markWizardComplete } from "../services/samplePolicyService";
import {
  ArrowRight,
  Square,
  Loader2,
  CheckCircle,
  Sparkles,
  FileText,
  ArrowLeft,
  Save,
  RefreshCw,
  Star,
  Clock,
  ShieldCheck,
} from "lucide-react";
import RevisrLayout from "../components/RevisrLayout";
import GenerationStream from "../components/create-policy/GenerationStream";
import { BrandingService } from "@foundation/services/brandingService";
import type { BoostPolicyBrandingData, BoostPolicyTag } from "../types/boostPolicy";
import { DocumentRegenerateDialog } from "@/features/revisr/components/editor/DocumentRegenerateDialog";
import { RegenerationDialog } from "@/features/revisr/components/editor/RegenerationDialog";
import { PolicyFeedbackModal } from "@/features/revisr/components/feedback/PolicyFeedbackModal";
import { policyFeedbackExists } from "@/features/revisr/services/policyFeedbackService";
import { regenerateSection, regenerateDocumentStream } from "@/features/revisr/services/policyGenerationService";
import { parseMarkdownToSections, mergeSessionResultContent, renumberSectionHeadings } from "../utils/boostPolicySession";

const SESSION_DATA_KEY = "revisr_brand_policy_data";
const SESSION_RESULT_KEY = "revisr_brand_policy_result";
const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

type GenerationStatus = "idle" | "generating" | "completed" | "error" | "stopped";

type StreamPayload = {
  selectedLibraryDocId: string;
  selectedSkillIds: string[];
  policyName: string;
  organizationContext: string;
  industryTags: BoostPolicyTag[];
  regionTags: BoostPolicyTag[];
  workforceSize: string;
  brandingData: BoostPolicyBrandingData | null;
  selectedTemplate?: unknown;
  // Document classification (Public / Internal Use / Private / Confidential)
  // from the Q&A Tier 0 answer — drives the footer label. Defaults to Public.
  documentClassification?: string;
};

/** Read the session payload once synchronously — returns null if absent/invalid. */
function readSessionPayload(): StreamPayload | null {
  const stored = sessionStorage.getItem(SESSION_DATA_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as StreamPayload;
  } catch {
    return null;
  }
}

export default function BoostPolicyGenerating() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useUser();
  const { toast } = useToast();

  const policyRecordId = searchParams.get("id") || sessionStorage.getItem("boost_policy_record_id");

  const [content, setContent] = useState("");
  const [status, setStatus] = useState<GenerationStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [alreadyGenerated, setAlreadyGenerated] = useState(false);
  const [isNavigatingToEditor, setIsNavigatingToEditor] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // resumeCheckDone: skip DB check if there is no record ID to look up
  const [resumeCheckDone, setResumeCheckDone] = useState(!policyRecordId);

  // ── Post-generation AI actions (regenerate document / section, feedback) ────
  const [showDocRegenDialog, setShowDocRegenDialog] = useState(false);
  const [isRegeneratingDoc, setIsRegeneratingDoc] = useState(false);
  const [showSectionRegenDialog, setShowSectionRegenDialog] = useState(false);
  const [isRegeneratingSection, setIsRegeneratingSection] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackAlreadyGiven, setFeedbackAlreadyGiven] = useState(false);

  // ── Policy approval workflow ──────────────────────────────────────────────
  // When the POLICY_APPROVAL_REQUIRED flag is on and the viewer is a member (not
  // org admin), the Save button becomes "Request Approval" and the auto-persist
  // on generation completion leaves the policy at 'generated' instead of marking
  // it 'completed'. approvalState mirrors the record's metadata.approval.status
  // so a cross-tab resume shows the right button (Pending / Rejected / approved).
  const { approvalRequired, isOrgAdmin } = usePolicyApproval();
  const memberGated = approvalRequired && !isOrgAdmin;
  const [approvalState, setApprovalState] = useState<PolicyApprovalState>('none');

  const docRegenAbortRef = useRef<AbortController | null>(null);
  const sectionRegenAbortRef = useRef<AbortController | null>(null);
  const pendingNavigationRef = useRef<boolean>(false);
  // True when content has been regenerated since the last persist, so the Next
  // navigation knows it must save the new markdown to the DB before opening the
  // editor (the editor loads from policy_markdown_url, not React state).
  const contentDirtyRef = useRef<boolean>(false);

  // Derived: can the user give feedback (needs saved policy + auth)
  const canGiveFeedback = !!policyRecordId && !!user?.id;

  // ── Display state (UI-only, does not affect generation stability) ──────────
  const [policyName, setPolicyName] = useState<string>(() => {
    const p = readSessionPayload();
    return p?.policyName || "Policy";
  });
  const [sidebarInfo, setSidebarInfo] = useState<{ hasLibraryDoc: boolean; skillsCount: number }>(() => {
    const p = readSessionPayload();
    return {
      hasLibraryDoc: !!(p?.selectedLibraryDocId),
      skillsCount: Array.isArray(p?.selectedSkillIds) ? p.selectedSkillIds.length : 0,
    };
  });
  const [generationBranding, setGenerationBranding] = useState<{
    fontFamily?: string;
  } | undefined>(() => {
    const ff = readSessionPayload()?.brandingData?.font_family;
    return ff ? { fontFamily: ff } : undefined;
  });

  // ── Stable payload ref ─────────────────────────────────────────────────────
  // Populated from session (same-tab) OR reconstructed from DB (any tab).
  // Using a ref avoids re-creating startGeneration when the data arrives asynchronously.
  const payloadRef = useRef<StreamPayload | null>(null);

  // Initialise synchronously from session so it is available immediately on
  // same-tab navigations (before the DB check completes).
  if (payloadRef.current === null) {
    payloadRef.current = readSessionPayload();
  }

  const abortControllerRef = useRef<AbortController | null>(null);
  const hasStartedRef = useRef(false);
  const contentRef = useRef("");

  // Org branding font for streamed policy preview (payload snapshot or live org branding)
  useEffect(() => {
    const syncFont = async () => {
      const fromPayload = payloadRef.current?.brandingData?.font_family;
      if (fromPayload) {
        setGenerationBranding({ fontFamily: fromPayload });
        return;
      }
      const orgId = getActiveWorkspaceId();
      if (!orgId) return;
      try {
        const orgBranding = await BrandingService.getOrganizationBranding(orgId);
        if (orgBranding?.font_family) {
          setGenerationBranding({ fontFamily: orgBranding.font_family });
        }
      } catch {
        // Non-fatal — GenerationStream defaults to Inter
      }
    };
    void syncFont();

    const onBrandingUpdated = (e: Event) => {
      const { orgId } = (e as CustomEvent<{ orgId: string }>).detail ?? {};
      if (orgId && orgId === getActiveWorkspaceId()) {
        void syncFont();
      }
    };
    window.addEventListener('branding-updated', onBrandingUpdated);
    return () => window.removeEventListener('branding-updated', onBrandingUpdated);
  }, [resumeCheckDone]);

  // ── DB check: fetch record, fill payloadRef from DB if session is empty ───
  useEffect(() => {
    if (!policyRecordId) return;

    const checkExisting = async () => {
      try {
        const record = await getBoostPolicyById(policyRecordId);
        const auditResults = record.audit_results ?? {};

        // DB is the authoritative source for policyName
        const dbPolicyName = record.policy_name && record.policy_name !== "Untitled"
          ? record.policy_name
          : null;
        if (dbPolicyName) setPolicyName(dbPolicyName);

        // Fetch full template so pages are available for the editor (cross-tab / new browser).
        // This is non-fatal — fall back to stub template if the fetch fails.
        let fullTemplate: { id: string; pages: unknown[] | null; sectionNames: string[] } | null = null;
        if (auditResults.selectedTemplateId) {
          try {
            fullTemplate = await loadPolicyTemplateById(auditResults.selectedTemplateId as string);
          } catch {
            // Non-fatal — editor's loadFn will also attempt this on load
          }
          if (!fullTemplate) {
            fullTemplate = { id: auditResults.selectedTemplateId as string, pages: null, sectionNames: [] };
          }
        }

        // Reconstruct stream payload from DB record so the page works without session
        const dbPayload: StreamPayload = {
          selectedLibraryDocId: auditResults.selectedKnowledgeDocument?.id ?? "",
          selectedSkillIds: auditResults.selectedSkillIds ?? [],
          policyName: record.policy_name ?? "",
          organizationContext: record.generation_prompt ?? "",
          industryTags: auditResults.industryTags ?? [],
          regionTags: auditResults.regionTags ?? [],
          workforceSize: auditResults.workforceSize ?? "",
          brandingData: record.branding_data ?? null,
          // Include full template (with pages) so re-generation and session restore both work
          selectedTemplate: fullTemplate ?? undefined,
        };

        // Fill the ref only if session didn't already populate it
        if (!payloadRef.current) {
          payloadRef.current = dbPayload;
        }

        // Always update sidebar from DB (source of truth for new-tab flows)
        setSidebarInfo({
          hasLibraryDoc: !!dbPayload.selectedLibraryDocId,
          skillsCount: dbPayload.selectedSkillIds.length,
        });

        if (dbPayload.brandingData?.font_family) {
          setGenerationBranding({ fontFamily: dbPayload.brandingData.font_family });
        }

        if (record.current_step >= 5) {
          // Generation was previously completed — restore content using a priority chain:
          // 1. knowledge_docs storage bucket via policy_markdown_url (source of truth)
          // 2. sessionStorage same-tab cache (fallback only)
          // 3. Fall through to re-generate if both are unavailable
          //
          // The markdown bucket is the source of truth: it is refreshed on
          // generation AND on every editor save (saveFn re-serializes the edited
          // pages to markdown). The sessionStorage cache is a same-tab fast path
          // that is NOT refreshed when the editor saves — reading it first showed
          // stale, pre-edit markdown here (edits and removed headings appeared to
          // be ignored, while the editor itself loaded the fresh saved state).
          // Read the bucket first so the generation view always reflects the
          // latest save; use the session cache only if the bucket has nothing yet.

          let restoredContent = "";

          // Priority 1: storage bucket — always reflects the latest save (any tab/browser).
          if (record.policy_markdown_url) {
            const savedResult = await fetchSavedContent(policyRecordId);
            if (savedResult?.content) restoredContent = savedResult.content;
          }

          // Priority 2: same-tab session cache — fallback when the bucket is empty.
          if (!restoredContent) {
            const savedResult = sessionStorage.getItem(SESSION_RESULT_KEY);
            if (savedResult) {
              try {
                const parsed = JSON.parse(savedResult) as Record<string, unknown>;
                if (typeof parsed.fullContent === "string" && parsed.fullContent) {
                  restoredContent = parsed.fullContent;
                } else if (Array.isArray(parsed.sections)) {
                  restoredContent = (parsed.sections as { title: string; content: string }[])
                    .map((s) => `## ${s.title}\n\n${s.content}`)
                    .join("\n\n");
                }
              } catch {
                // ignore parse errors
              }
            }
          }

          if (restoredContent) {
            // Ensure SESSION_RESULT_KEY is populated so BrandedProposalEditor can load
            // without redirecting to the review page. Needed when content was restored
            // from the storage bucket (new-tab flow — no session data).
            const existingSession = sessionStorage.getItem(SESSION_RESULT_KEY);
            if (!existingSession) {
              const editorState = await fetchEditorContent(policyRecordId).catch(() => null);
              // Classification stored in revisr_final_policies.metadata at generation
              // time. Seeds the footer so a cross-tab / new-browser resume shows the
              // user's Q&A classification instead of the hardcoded "Public".
              const metaClassification =
                (record.metadata?.classification as string | undefined) ?? 'Public';
              let sessionPayload: Record<string, unknown>;
              if (editorState && Array.isArray(editorState.pages) && editorState.pages.length > 0) {
                // Exact branded editor state — render the editor without re-converting markdown.
                sessionPayload = {
                  sections: [],
                  branding: record.branding_data ?? null,
                  title: record.policy_name || "Policy",
                  pages: editorState.pages,
                  zoom: editorState.zoom ?? 1,
                  selectedTemplate: fullTemplate,
                  footer: {
                    ...editorState.footer,
                    classification: editorState.footer?.classification ?? metaClassification,
                  },
                };
              } else {
                // Never edited — build sections from the generated markdown.
                sessionPayload = {
                  sections: parseMarkdownToSections(restoredContent),
                  branding: record.branding_data ?? null,
                  title: record.policy_name || "Policy",
                  fullContent: restoredContent,
                  selectedTemplate: fullTemplate,
                  footer: { classification: metaClassification },
                };
              }
              sessionStorage.setItem(SESSION_RESULT_KEY, JSON.stringify(sessionPayload));
            }

            setContent(restoredContent);
            setAlreadyGenerated(true);
            setStatus("completed");
            // Reflect any prior approval decision so the resume shows the right
            // button (Pending Approval / Rejected / approved) instead of defaulting
            // to Request Approval.
            setApprovalState(getApprovalState(record));
          }
          // Priority 3: no saved content found — fall through so generation re-runs
        }
      } catch {
        // Fetch failed — proceed with whatever session payload is available (may be null)
      } finally {
        setResumeCheckDone(true);
      }
    };

    checkExisting();
  }, [policyRecordId]);

  // ── Generation ─────────────────────────────────────────────────────────────
  // startGeneration reads payloadRef.current directly (not a dep) so its
  // identity stays stable and never aborts an in-progress stream.
  const startGeneration = useCallback(async () => {
    const payload = payloadRef.current;

    if (!payload || !payload.selectedLibraryDocId) {
      setError("No generation data found. Please go back and fill in the details.");
      setStatus("error");
      return;
    }

    try {
      setStatus("generating");
      setContent("");
      setError(null);

      const controller = new AbortController();
      abortControllerRef.current = controller;

      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (user?.id) headers["x-user-id"] = user.id;
      const orgId = getActiveWorkspaceId();
      if (orgId) headers["x-org-id"] = orgId;

      const response = await fetch(`${API_BASE_URL}/api/revisr/brand-policy/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({ ...payload, policyRecordId: policyRecordId ?? undefined, org_id: orgId ?? undefined }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error((errData as { error?: string }).error || `Server error: ${response.status}`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error("Failed to get response stream reader");

      let fullContent = "";
      // Buffer to accumulate incomplete SSE lines across reads.
      // Large events (e.g. the 'complete' event with ~19k chars of JSON) are
      // often split across multiple TCP chunks. Without buffering, the partial
      // line is silently dropped and the 'complete' event is never processed,
      // leaving the UI stuck on "Generating..." indefinitely.
      let lineBuffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        // Keep the last (possibly incomplete) line in the buffer
        lineBuffer = lines.pop() ?? "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;

          let data: Record<string, unknown>;
          try {
            data = JSON.parse(line.slice(6));
          } catch {
            continue;
          }

          if (data.type === "content") {
            fullContent += data.text as string;
            contentRef.current = fullContent;
            setContent(fullContent);
          } else if (data.type === "complete") {
            fullContent = (data.fullContent as string) || fullContent;
            setContent(fullContent);
            setStatus("completed");

            // If user arrived via the onboarding flow, mark the sample wizard complete
            if (sessionStorage.getItem('boost_onboarding_mode') === 'true') {
              const orgId = getActiveWorkspaceId();
              markWizardComplete(user?.id ?? '', orgId ?? '').catch(console.error);
              sessionStorage.removeItem('boost_onboarding_mode');
            }

            // Use backend-resolved pages (with placeholders filled) if available,
            // otherwise fall back to the original frontend-loaded template pages.
            const resolvedPages = data.resolvedPages as unknown[] | null;
            const originalTemplate = (payload as { selectedTemplate?: { id?: string; pages?: unknown[] | null; sectionNames?: string[] } }).selectedTemplate ?? null;
            const templateForSession = originalTemplate
              ? {
                  ...originalTemplate,
                  pages: (Array.isArray(resolvedPages) && resolvedPages.length > 0)
                    ? resolvedPages
                    : originalTemplate.pages,
                }
              : null;

            // Cache in session for same-tab resume
            sessionStorage.setItem(
              SESSION_RESULT_KEY,
              JSON.stringify({
                sections: data.sections,
                branding: data.branding,
                title: payload.policyName,
                fullContent,
                selectedTemplate: templateForSession,
                // Editable footer seeded from the Q&A classification answer +
                // policy name + org name + year. BrandedProposalEditor reads
                // these via applySessionInput and lets the user edit them.
                footer: {
                  classification: payload.documentClassification ?? 'Public',
                  policyName: payload.policyName,
                  orgName: payload.brandingData?.brand_title ?? undefined,
                  date: String(new Date().getFullYear()),
                },
                includeCoverToc: true,
              }),
            );

            // Persist to storage bucket so content survives across tabs/browsers.
            // Also updates policy_markdown_url and sets current_step to 5 (only if < 6).
            // Include resolvedPages so the editor can load them from DB without re-resolving.
            // Mark the policy status as 'completed' now that generation is done.
            if (policyRecordId) {
              // When the approval flag is on and the viewer is a member, leave the
              // policy at 'generated' (not 'completed') so it cannot be exported
              // until an admin approves. Org admins / flag-off keep current behavior.
              const completionStatus = memberGated ? 'generated' : 'completed';
              updateBoostPolicyStep(policyRecordId, { current_step: 5, status: completionStatus }).catch((err) => {
                console.error("[BoostPolicyGenerating] Failed to mark policy as completed:", err);
              });
              if (memberGated) setApprovalState('none');
              const pagesToPersist = templateForSession?.pages ?? undefined;
              saveGeneratedContent(policyRecordId, fullContent, payload.policyName, pagesToPersist).catch((err) => {
                console.error("[BoostPolicyGenerating] Failed to persist generated content:", err);
              });
            }
          } else if (data.type === "error") {
            throw new Error((data.message as string) || "Generation failed");
          }
        }
      }
    } catch (err) {
      if ((err as Error).name === "AbortError") {
        setStatus("stopped");
        return;
      }
      const message = err instanceof Error ? err.message : "Generation failed";
      setError(message);
      setStatus("error");
      toast({ title: "Generation failed", description: message, variant: "destructive" });
    }
  }, [user?.id, toast, policyRecordId]);

  useEffect(() => {
    if (!resumeCheckDone || alreadyGenerated) return;

    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startGeneration();
    }
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [resumeCheckDone, alreadyGenerated, startGeneration]);

  const handleStop = () => {
    abortControllerRef.current?.abort();
    setStatus("stopped");
    const partialContent = contentRef.current;
    if (policyRecordId && partialContent.trim()) {
      const payload = payloadRef.current;
      const name = payload?.policyName || policyName || "Policy";
      saveGeneratedContent(policyRecordId, partialContent, name).catch((err) => {
        console.error("[BoostPolicyGenerating] Failed to persist partial content on stop:", err);
      });
    }
  };

  const handleContinueToEdit = async () => {
    // Prompt for feedback before navigating (if not already given)
    if (canGiveFeedback && !feedbackAlreadyGiven && !showFeedbackModal) {
      pendingNavigationRef.current = true;
      setShowFeedbackModal(true);
      return;
    }
    await proceedToEditor();
  };

  const proceedToEditor = async () => {
    setIsNavigatingToEditor(true);
    // Ensure SESSION_RESULT_KEY is populated so BrandedProposalEditor doesn't
    // redirect back. This is critical when generation was stopped early — the
    // "complete" SSE event was never received, so session was never written.
    const existing = sessionStorage.getItem(SESSION_RESULT_KEY);
    if (!existing && content.trim()) {
      const payload = payloadRef.current;
      sessionStorage.setItem(
        SESSION_RESULT_KEY,
        JSON.stringify({
          sections: parseMarkdownToSections(content),
          branding: payload?.brandingData ?? null,
          title: payload?.policyName || policyName || "Policy",
          fullContent: content,
          selectedTemplate: (payload as { selectedTemplate?: unknown } | null)?.selectedTemplate ?? null,
        }),
      );
    }

    // Copy the server-resolved template pages (including cover page) from the RESULT key
    // into the DATA key. BrandedProposalEditor's applySessionInput() reads the DATA key
    // and uses its selectedTemplate to override the loadFn result. Without this, the editor
    // loads fresh unresolved pages from the bucket via loadPolicyTemplateById, and the
    // cover page still shows {{placeholder}} tokens.
    try {
      const resultRaw = sessionStorage.getItem(SESSION_RESULT_KEY);
      const dataRaw = sessionStorage.getItem(SESSION_DATA_KEY);
      if (resultRaw && dataRaw) {
        const result = JSON.parse(resultRaw) as { selectedTemplate?: { pages?: unknown[] } };
        const data = JSON.parse(dataRaw) as { selectedTemplate?: { pages?: unknown[] } };
        const resolvedPages = result.selectedTemplate?.pages;
        if (Array.isArray(resolvedPages) && resolvedPages.length > 0 && data.selectedTemplate) {
          data.selectedTemplate.pages = resolvedPages;
          sessionStorage.setItem(SESSION_DATA_KEY, JSON.stringify(data));
        }
      }
    } catch (err) {
      console.error("[BoostPolicyGenerating] Failed to sync resolved pages to data key:", err);
    }

    // If the document/section was regenerated after generation completed, the new
    // markdown only lives in React state + session. Persist it to the DB so the
    // editor (which loads from policy_markdown_url) shows the regenerated content
    // instead of the original generated markdown.
    if (contentDirtyRef.current && policyRecordId && content.trim()) {
      try {
        await persistContent(content);
      } catch (err) {
        console.error("[BoostPolicyGenerating] Failed to persist regenerated content before editor:", err);
        toast({
          title: "Save failed",
          description: "Unable to save your changes. Please use the Save button before continuing.",
          variant: "destructive",
        });
        setIsNavigatingToEditor(false);
        return;
      }
    }

    if (policyRecordId) {
      try {
        // Gated members must NOT be marked 'completed' here — that would bypass
        // the approval gate (completed is exportable) and mislabel the policy in
        // Your Policies. Keep the existing status (generated / pending_approval)
        // and only advance the step. Org admins / flag-off keep the old behaviour.
        const stepUpdate = memberGated
          ? { current_step: 6 }
          : { current_step: 6, status: 'completed' };
        await updateBoostPolicyStep(policyRecordId, stepUpdate);
      } catch (err) {
        console.error("[BoostPolicyGenerating] Failed to update step for editor navigation:", err);
      }
    }
    navigate(`/revisr/brand-policy/editor${policyRecordId ? `?id=${policyRecordId}` : ""}`);
  };

  /**
   * Persist the current markdown to the DB (policy_markdown_url), preserving the
   * resolved cover/TOC pages cached in the session. The editor loads from the DB,
   * so this is what makes regenerated/edited content survive navigation.
   * Clears the dirty flag on success.
   */
  const persistContent = async (markdown: string): Promise<void> => {
    if (!policyRecordId || !markdown.trim()) return;
    const payload = payloadRef.current;
    const resultRaw = sessionStorage.getItem(SESSION_RESULT_KEY);
    let resolvedPages: unknown[] | undefined;
    if (resultRaw) {
      try {
        const result = JSON.parse(resultRaw) as { selectedTemplate?: { pages?: unknown[] } };
        if (Array.isArray(result.selectedTemplate?.pages) && result.selectedTemplate.pages.length > 0) {
          resolvedPages = result.selectedTemplate.pages;
        }
      } catch {
        // ignore
      }
    }
    await saveGeneratedContent(policyRecordId, markdown, payload?.policyName || policyName, resolvedPages);
    contentDirtyRef.current = false;
  };

  const handleSave = async () => {
    if (!policyRecordId || !content.trim()) return;
    setIsSaving(true);
    try {
      await persistContent(content);
      toast({ title: "Policy saved", description: "Your policy has been saved successfully." });
      // Prompt for feedback after save (if not already given)
      if (canGiveFeedback && !feedbackAlreadyGiven) {
        setShowFeedbackModal(true);
      }
    } catch (err) {
      console.error("[BoostPolicyGenerating] Manual save failed:", err);
      toast({ title: "Save failed", description: "Unable to save the policy. Please try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  // Member → request org admin approval (approval workflow). Persists the current
  // markdown first so the admin reviews the latest content, then flips the policy
  // to 'pending_approval'. Replaces the Save button when the approval flag is on
  // and the viewer is a member whose policy has not yet been approved.
  const handleRequestApproval = async () => {
    if (!policyRecordId || !content.trim()) return;
    setIsSaving(true);
    try {
      await persistContent(content);
      await requestApproval(policyRecordId);
      setApprovalState('pending');
      toast({
        title: "Approval requested",
        description: "An organisation admin will review your policy. You'll be able to save and export once it's approved.",
      });
    } catch (err) {
      console.error("[BoostPolicyGenerating] Request approval failed:", err);
      toast({ title: "Request failed", description: "Unable to request approval. Please try again.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Post-generation AI actions ─────────────────────────────────────────────
  // Sections parsed from the current markdown — drives the section picker.
  const sections = useMemo(() => parseMarkdownToSections(content), [content]);

  // Clamp the selected section index whenever the section list changes.
  useEffect(() => {
    if (selectedSectionIndex > sections.length - 1) {
      setSelectedSectionIndex(sections.length > 0 ? sections.length - 1 : 0);
    }
  }, [sections.length, selectedSectionIndex]);

  // Abort any in-flight regeneration on unmount.
  useEffect(() => {
    return () => {
      docRegenAbortRef.current?.abort();
      sectionRegenAbortRef.current?.abort();
    };
  }, []);

  const handleRegenerateDocument = async (userContext: string) => {
    if (isRegeneratingDoc) return;
    const controller = new AbortController();
    docRegenAbortRef.current = controller;
    setIsRegeneratingDoc(true);
    try {
      const newMarkdown = await regenerateDocumentStream(
        policyRecordId ?? undefined,
        { fullDocumentContext: content, additionalPrompt: userContext || undefined },
        { onChunk: (full) => setContent(full), signal: controller.signal },
        user?.id,
        getActiveWorkspaceId() ?? undefined,
      );
      if (controller.signal.aborted) return;
      if (!newMarkdown) throw new Error("No content returned from AI");
      // Refresh the session cache and flag the content dirty so Next persists it.
      sessionStorage.setItem(
        SESSION_RESULT_KEY,
        mergeSessionResultContent(
          sessionStorage.getItem(SESSION_RESULT_KEY),
          newMarkdown,
          payloadRef.current?.policyName || policyName,
        ),
      );
      contentDirtyRef.current = true;
      setShowDocRegenDialog(false);
      toast({ title: "Document regenerated", description: "The whole policy has been rewritten." });
    } catch (err) {
      if (controller.signal.aborted) return; // intentional cancel/unmount — no error toast
      const message = err instanceof Error ? err.message : "Regeneration failed";
      toast({ title: "Regeneration failed", description: message, variant: "destructive" });
    } finally {
      if (docRegenAbortRef.current === controller) docRegenAbortRef.current = null;
      setIsRegeneratingDoc(false);
    }
  };

  const handleRegenerateSection = async (composedPrompt: string) => {
    if (isRegeneratingSection) return;
    const section = sections[selectedSectionIndex];
    if (!section) return;
    const controller = new AbortController();
    sectionRegenAbortRef.current = controller;
    setIsRegeneratingSection(true);
    try {
      const regenerated = await regenerateSection(
        policyRecordId ?? undefined,
        {
          sectionIndex: selectedSectionIndex,
          sectionTitle: section.title,
          additionalPrompt: composedPrompt,
          fullDocumentContext: content,
        },
        user?.id,
        getActiveWorkspaceId() ?? undefined,
        controller.signal,
      );
      if (controller.signal.aborted) return;

      let rebuilt: string;
      if (regenerated.action === "remove") {
        // The prompt asked to delete this section — rebuild the document without it.
        // Renumber the remaining `N.` heading prefixes so they stay contiguous
        // (mirrors the branded editor), otherwise removing e.g. section 4 leaves
        // a gap in the generation view: 1, 2, 3, 5, ...
        rebuilt = renumberSectionHeadings(
          sections
            .filter((_, i) => i !== selectedSectionIndex)
            .map((s) => `## ${s.title.replace(/^#+\s*/, "")}\n\n${s.content}`)
            .join("\n\n"),
        );
      } else {
        if (!regenerated.content) throw new Error("No content returned from AI");

        // Strip a leading heading line — the rebuild re-applies the section heading.
        let cleanContent = regenerated.content.trim();
        const headingMatch = cleanContent.match(/^#{1,6}\s+.+\n*/);
        if (headingMatch) {
          cleanContent = cleanContent.slice(headingMatch[0].length).trim();
        }

        // Rebuild the full document with the regenerated section spliced in.
        // Renumber so a regenerated title that dropped/changed its number prefix
        // stays consistent with the rest of the document.
        rebuilt = renumberSectionHeadings(
          sections
            .map((s, i) => {
              const body = i === selectedSectionIndex ? cleanContent : s.content;
              return `## ${s.title.replace(/^#+\s*/, "")}\n\n${body}`;
            })
            .join("\n\n"),
        );
      }
      setContent(rebuilt);
      // Refresh the session cache and flag the content dirty so Next persists it.
      sessionStorage.setItem(
        SESSION_RESULT_KEY,
        mergeSessionResultContent(
          sessionStorage.getItem(SESSION_RESULT_KEY),
          rebuilt,
          payloadRef.current?.policyName || policyName,
        ),
      );
      contentDirtyRef.current = true;
      setShowSectionRegenDialog(false);
      if (regenerated.action === "remove") {
        toast({ title: "Section removed", description: `"${section.title}" has been removed from the document.` });
      } else {
        toast({ title: "Section regenerated", description: `"${section.title}" has been updated.` });
      }
    } catch (err) {
      if (controller.signal.aborted) return;
      const message = err instanceof Error ? err.message : "Regeneration failed";
      toast({ title: "Regeneration failed", description: message, variant: "destructive" });
    } finally {
      if (sectionRegenAbortRef.current === controller) sectionRegenAbortRef.current = null;
      setIsRegeneratingSection(false);
    }
  };

  const showPostGenActions =
    status === "completed" || (status === "stopped" && content.length > 0);

  // Check if feedback already submitted for this policy
  useEffect(() => {
    if (!showPostGenActions || !policyRecordId || !user?.id) return;
    policyFeedbackExists(policyRecordId, user.id, getActiveWorkspaceId() ?? undefined)
      .then((exists) => setFeedbackAlreadyGiven(exists))
      .catch(() => setFeedbackAlreadyGiven(false));
  }, [showPostGenActions, policyRecordId, user?.id]);

  // ── Loading state while DB check is in progress ────────────────────────────
  if (!resumeCheckDone) {
    return (
      <RevisrLayout hideMobileBar>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </RevisrLayout>
    );
  }

  return (
    <RevisrLayout hideMobileBar>
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <Button
                  variant="ghost"
                  onClick={() => {
                    const base = sessionStorage.getItem('boost_policy_base_route') || '/revisr/boost-policy';
                    navigate(`${base}?step=review${policyRecordId ? `&id=${policyRecordId}` : ""}`);
                  }}
                  className="gap-2 mb-2"
                  disabled={status === "generating"}
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Review
                </Button>
                <h1 className="text-xl md:text-2xl font-bold text-foreground">Generating Policy</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  AI is generating your <strong>{policyName}</strong> from the selected library document
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {status === "generating" && (
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700 border-blue-200">
                    <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                    Generating...
                  </Badge>
                )}
                {status === "completed" && (
                  <Badge variant="secondary" className="bg-green-50 text-green-700 border-green-200">
                    <CheckCircle className="h-3 w-3 mr-1.5" />
                    Generated
                  </Badge>
                )}
                {status === "stopped" && (
                  <Badge variant="secondary" className="bg-amber-50 text-amber-700 border-amber-200">
                    <Square className="h-3 w-3 mr-1.5" />
                    Stopped
                  </Badge>
                )}
                {status === "error" && (
                  <Badge variant="secondary" className="bg-red-50 text-red-700 border-red-200">
                    Generation Error
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-6 items-start">
            {/* Left column — generation details */}
            <div className="w-full lg:w-56 lg:shrink-0 space-y-4">
              <Card className="border border-border bg-card shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    Generation Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Policy:</span>
                    <p className="font-medium text-foreground truncate">{policyName}</p>
                  </div>
                  {sidebarInfo.hasLibraryDoc && (
                    <div>
                      <span className="text-muted-foreground">Source:</span>
                      <p className="font-medium text-foreground truncate">Library document</p>
                    </div>
                  )}
                  {sidebarInfo.skillsCount > 0 && (
                    <div>
                      <span className="text-muted-foreground">AI Skills:</span>
                      <p className="font-medium text-foreground">
                        {sidebarInfo.skillsCount} applied
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Center column — generated content */}
            <div className="w-full lg:flex-1 min-w-0">
              <Card className="border border-border bg-card shadow-sm">
                <CardHeader className="pb-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-base font-semibold flex items-center gap-2 shrink-0 whitespace-nowrap">
                      <FileText className="h-4 w-4 text-primary" />
                      Generated Policy
                    </CardTitle>

                    {status === "generating" && (
                      <Button
                        onClick={handleStop}
                        variant="outline"
                        size="sm"
                        className="h-8 border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Square className="h-3.5 w-3.5 mr-1.5" />
                        Stop
                      </Button>
                    )}
                  </div>

                  {/* Full-width regenerate toolbar — two equal-width actions on one row */}
                  {showPostGenActions && (
                    <div className="flex flex-col sm:flex-row items-stretch gap-2 w-full">
                      <Button
                        onClick={() => setShowSectionRegenDialog(true)}
                        size="sm"
                        className="h-9 flex-1 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
                        disabled={isRegeneratingSection || sections.length === 0}
                      >
                        <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                        Regenerate section
                      </Button>
                      <Button
                        onClick={() => setShowDocRegenDialog(true)}
                        size="sm"
                        className="h-9 flex-1 bg-white text-gray-900 border border-gray-300 hover:bg-gray-50"
                        disabled={isRegeneratingDoc || !content.trim()}
                      >
                        <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                        Regenerate document
                      </Button>
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-0">
                  <GenerationStream
                    content={content}
                    isStreaming={status === "generating" || isRegeneratingDoc}
                    error={error}
                    maxHeight="calc(100vh - 350px)"
                    branding={generationBranding}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Right column — feedback (shown after generation completes) */}
            <div className="w-full lg:w-64 lg:shrink-0 space-y-4">
              {showPostGenActions && (
                <Card className="border border-primary/20 bg-primary/5 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2">
                      <Star className="h-3.5 w-3.5 text-yellow-500" />
                      Feedback
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {feedbackAlreadyGiven ? (
                      <div className="flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        <span>Feedback submitted</span>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground">
                          Rate this policy
                        </p>
                        <Button
                          onClick={() => setShowFeedbackModal(true)}
                          size="sm"
                          className="w-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2"
                          disabled={!canGiveFeedback}
                          title={canGiveFeedback ? undefined : "Policy must be saved to leave feedback"}
                        >
                          <Star className="h-3.5 w-3.5" />
                          Feedback
                        </Button>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>

          {/* Spacer so sticky bar doesn't overlap content */}
          {(status === "completed" || (status === "stopped" && content.length > 0)) && (
            <div className="h-24" />
          )}
        </div>

        {/* Sticky action footer — shown when generation is complete or stopped with content */}
        {(status === "completed" || (status === "stopped" && content.length > 0)) && (
          <div className="sticky bottom-0 z-20 bg-card border-t border-border shadow-lg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {memberGated && approvalState === 'pending' ? (
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-600">
                    <Clock className="h-4 w-4" />
                    Requested — an organisation admin will review your policy.
                  </div>
                ) : memberGated && approvalState === 'rejected' ? (
                  <div className="flex items-center gap-2 text-sm font-medium text-red-600">
                    <Square className="h-4 w-4" />
                    Policy was rejected — edit and request approval again.
                  </div>
                ) : memberGated && approvalState === 'approved' ? (
                  <div className="flex items-center gap-2 text-sm font-medium text-[#33b864]">
                    <ShieldCheck className="h-4 w-4" />
                    Policy approved — you can now save and export.
                  </div>
                ) : status === "completed" ? (
                  <div className="flex items-center gap-2 text-sm font-medium text-[#33b864]">
                    <CheckCircle className="h-4 w-4" />
                    Policy generated successfully — review the content above, then continue to the editor.
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm font-medium text-amber-600">
                    <Square className="h-4 w-4" />
                    Generation stopped — you can retry or continue with the content generated so far.
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {memberGated && approvalState !== 'approved' ? (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleRequestApproval}
                    disabled={isSaving || isNavigatingToEditor || !content.trim() || approvalState === 'pending'}
                    className="gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Requesting...
                      </>
                    ) : approvalState === 'pending' ? (
                      <>
                        <Clock className="h-4 w-4" />
                        Requested
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-4 w-4" />
                        Request Approval
                      </>
                    )}
                  </Button>
                ) : (
                  <Button
                    size="lg"
                    variant="outline"
                    onClick={handleSave}
                    disabled={isSaving || isNavigatingToEditor || !content.trim()}
                    className="gap-2"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                )}
                <Button
                  size="lg"
                  onClick={handleContinueToEdit}
                  className="gap-2 min-w-[140px] bg-[#33b864] hover:bg-[#2da358] text-white"
                  disabled={isNavigatingToEditor || isSaving}
                >
                  {isNavigatingToEditor ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Opening...
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* AI action dialogs */}
        <DocumentRegenerateDialog
          open={showDocRegenDialog}
          onOpenChange={setShowDocRegenDialog}
          onRegenerate={handleRegenerateDocument}
          isRegenerating={isRegeneratingDoc}
        />
        <RegenerationDialog
          open={showSectionRegenDialog}
          onOpenChange={setShowSectionRegenDialog}
          sectionTitle={sections[selectedSectionIndex]?.title ?? ""}
          onRegenerate={handleRegenerateSection}
          isRegenerating={isRegeneratingSection}
          sections={sections}
          selectedSectionIndex={selectedSectionIndex}
          onSectionChange={setSelectedSectionIndex}
        />
        {showFeedbackModal && policyRecordId && user?.id && (
          <PolicyFeedbackModal
            open={showFeedbackModal}
            policyId={policyRecordId}
            userId={user.id}
            orgId={getActiveWorkspaceId() ?? undefined}
            onSubmitted={() => setFeedbackAlreadyGiven(true)}
            onClose={() => {
              setShowFeedbackModal(false);
              if (pendingNavigationRef.current) {
                pendingNavigationRef.current = false;
                void proceedToEditor();
              }
            }}
          />
        )}
      </div>
    </RevisrLayout>
  );
}
